import React from 'react';
import { P3Configuration } from 'models/interfaces/P3Configurations';
import { configurationTemplateHeadings, createTemplateRow } from './P3TemplateListConfig';
import Table from '../Table/TableComponent';
import memoize from 'memoize-one';
import { TableRow } from 'models/interfaces/Table';
import { toast } from 'react-toastify';
import { confirmDuplication, confirmDeletion } from 'util/confirmation';

interface P3TemplateListProps {
  templates: P3Configuration[];
  showAssignTemplate: boolean;
  editTemplate: (template?: P3Configuration) => void;
  deleteTemplate: (templateId: string) => void;
  duplicateTemplate: (templateId: string) => void;
  assignTemplate: (template: P3Configuration) => void;
}

const P3TemplateList: React.FC<P3TemplateListProps> = ({
  templates,
  showAssignTemplate,
  deleteTemplate,
  duplicateTemplate,
  editTemplate,
  assignTemplate
}: P3TemplateListProps) => {
  const onEditTemplate = (templateId: string) => {
    editTemplate(templatesMap.get(templateId));
  };

  const onAssignTemplate = (templateId: string) => {
    const template = templatesMap.get(templateId);
    // raise hell if template is missing
    if (!template) {
      console.log(`Couldn't find templateId ${templateId} in templatesMap.`);
      toast.error('Something went wrong duplicating the template.');
      return;
    }
    assignTemplate(template);
  };

  const onDuplicateTemplate = async (templateId: string) => {
    // get campaign by id
    const template = templatesMap.get(templateId);
    // raise hell if template is missing
    if (!template) {
      console.log(`Couldn't find templateId ${templateId} in templatesMap.`);
      toast.error('Something went wrong duplicating the template.');
      return;
    }
    // duplicate template
    const confirm = confirmDuplication('configuration');
    return confirm ? await duplicateTemplate(templateId) : null;
  };

  const onDeleteTemplate = async (templateId: string) => {
    // get template by id
    const template = templatesMap.get(templateId);
    // raise hell if template is missing
    if (!template) {
      console.log(`Couldn't find templateId ${templateId} in templatesMap.`);
      toast.error('Something went wrong deleting the template.');
      return;
    }
    // delete template
    const confirm = confirmDeletion('configuration');
    return confirm ? await deleteTemplate(templateId) : null;
  };

  const buildRenderDataFromProps = (templateConfigs: P3Configuration[]) => {
    return templateConfigs.reduce<[Map<string, P3Configuration>, TableRow[]]>(
      ([map, rows], x) => {
        // shallow copy template to save from disappearing due to no-cache
        //console.log(`buildRenderFrom`);
        const copy: P3Configuration = { ...x };

        const templateIsEditable = true; //setting to true because these are templates not associated to any campaigns.
        // add copy to map
        map.set(copy.p3_configuration_id, copy);
        // create template tr and add to array
        const tr = createTemplateRow(
          copy,
          onEditTemplate,
          onDuplicateTemplate,
          onDeleteTemplate,
          onAssignTemplate,
          showAssignTemplate,
          templateIsEditable
        );
        rows.push(tr);

        return [map, rows];
      },
      [new Map<string, P3Configuration>(), new Array<TableRow>()]
    );
  };

  // memoize render data
  const memoizedRenderData = memoize(buildRenderDataFromProps);
  const [templatesMap, templateRows] = memoizedRenderData(templates);

  return <Table headings={configurationTemplateHeadings} data={templateRows} />;
};

export default P3TemplateList;
